import { useQuery } from '@tanstack/react-query';

import { useLanguage } from '@/contexts/language-context';
import { Label } from '@/components/ui/label';
import { listPaymentMethods, paymentMethodsQueryKey } from '@/lib/api/payment-methods';
import { paymentMethodLabel } from '@/routes/jobs/helpers';
import type { PaymentMethod } from '@/lib/api/jobs';

interface Props {
  businessId: string;
  offerAll: boolean;
  selectedIds: string[];
  onToggleOfferAll: (offerAll: boolean) => void;
  onToggleMethod: (id: string) => void;
  error?: string;
}

// Lets the owner pick which of the business's active payment methods an
// invoice offers. Defaults to "offer every active one" — mirrors the
// omit-means-all semantics of `CreateInvoiceRequest.payment_method_ids`.
export function PaymentMethodsField({
  businessId,
  offerAll,
  selectedIds,
  onToggleOfferAll,
  onToggleMethod,
  error,
}: Props) {
  const { t } = useLanguage();

  const methodsQuery = useQuery({
    queryKey: paymentMethodsQueryKey(businessId),
    queryFn: () => listPaymentMethods(businessId),
  });

  const activeMethods = (methodsQuery.data ?? []).filter((m) => m.is_active);

  return (
    <div className="flex flex-col gap-2">
      <Label>{t.invoicePaymentMethods}</Label>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={offerAll}
          onChange={(e) => onToggleOfferAll(e.target.checked)}
          data-testid="invoice-offer-all-methods"
        />
        {t.invoiceOfferAllMethods}
      </label>

      {!offerAll && (
        <div className="flex flex-col gap-1.5 pl-1">
          {methodsQuery.isPending ? (
            <p className="text-xs text-muted-foreground">{t.loading}</p>
          ) : activeMethods.length === 0 ? (
            <p className="text-xs text-muted-foreground">{t.invoiceNoPaymentMethods}</p>
          ) : (
            activeMethods.map((method) => (
              <label key={method.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selectedIds.includes(method.id)}
                  onChange={() => onToggleMethod(method.id)}
                  data-testid={`invoice-method-${method.id}`}
                />
                {method.label || paymentMethodLabel(method.type as PaymentMethod, t)}
              </label>
            ))
          )}
        </div>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
